import { Box, Button, Dialog, Slide, Typography } from '@mui/material';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import FlatwareIcon from "@mui/icons-material/Flatware";
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { CancelOutlined, CheckCircleOutlined, ContrastOutlined } from "@mui/icons-material";
import BaseInput from '../baseForm/BaseInput';
import { MAX_LENGTH_VALIDATION } from 'utils/errorMessages';
import { useUpdateMealIntakeMutation } from 'store/apis/MealsApi';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
});

const PERCENT_OPTIONS = [
  { value: 0, color: "#545454" },
  { value: 25, color: "#a80000" },
  { value: 50, color: "#dd9900" },
  { value: 75, color: "#00695c" },
  { value: 100, color: "#12850e" },
];

const MENU_CATEGORY_ORDER = ["Appetizer", "Entree", "Side", "Dessert", "Beverage"];

const buildIntakeMap = (order) => {
  const map = {};
  order?.orderItems?.forEach((item) => {
    map[item.id] = item?.intakePercentage ?? 0;
  });
  return map;
};

export default function RecordIntakeModal({
  open,
  currentOrder,
  handleClose
}) {

  const [intakeMap, setIntakeMap] = useState(() => buildIntakeMap(currentOrder));
  const methods = useForm({ 
    mode: "all", 
    shouldUnregister: false, 
    defaultValues: {
      intakeNote: currentOrder?.intakeNote ?? ""
    } 
  });

  const [
    setMealIntake,
    {
      isLoading: intakeSaving,
      isSuccess: intakeSuccess
    }
  ] = useUpdateMealIntakeMutation();

  useEffect(() => {
    if (!intakeSaving && intakeSuccess) {
      handleClose();
    }
  }, [intakeSaving, intakeSuccess]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (open) {
      reset({ intakeNote: currentOrder?.intakeNote ?? "" });
      setIntakeMap(buildIntakeMap(currentOrder));
    }
  }, [open, currentOrder, reset]);

  const groupedOrderItems = useMemo(() => {
    const items = currentOrder?.orderItems ?? [];
    return [...items].sort((a, b) => {
      const aIdx = MENU_CATEGORY_ORDER.indexOf(a.menuCategory);
      const bIdx = MENU_CATEGORY_ORDER.indexOf(b.menuCategory);
      return (aIdx === -1 ? MENU_CATEGORY_ORDER.length : aIdx) -
        (bIdx === -1 ? MENU_CATEGORY_ORDER.length : bIdx);
    });
  }, [currentOrder]);

  const handleSelectPercent = (itemId, value) => {
    setIntakeMap((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleQuickAction = (value) => {
    setIntakeMap((prev) => Object.fromEntries(Object.keys(prev).map((id) => [id, value])));
  };

  const onSubmit = (data) => {
    const payload = {
      tableSideOrderId: currentOrder?.id,
      intakeNote: data?.intakeNote ?? "",
      mealIntakeItems: Object.keys(intakeMap).map((key) => {
        return {
          tableSideOrderItemId: +key,
          intakePercentage: intakeMap[key]
        }
      })
    }

    setMealIntake(payload);    
  }

  const getAverageIntake = () => {
    if (Object.keys(intakeMap).length) {
      return `${(Object.values(intakeMap).reduce((acc, curr) => acc + curr, 0) / Object.keys(intakeMap).length).toFixed(2)}%`;
    } else {
      return "0.00%"
    }
  }

  return (
    <Dialog 
      open={open} 
      TransitionComponent={Transition}
      maxWidth="lg"  
    >
      <Box className="customScrollbar"
        sx={{
          backgroundColor: "#f4f9f9",
          border: "2px solid #737373",
          borderRadius: ".5rem",
          overflowY: "auto",
          width: "700px"
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flex: "1 0 100%",
            margin: ".25rem 0 1rem .5rem"
          }}
        >
          <Box
            display="flex"
            width="30%"
          >
            <FlatwareIcon 
              sx={{
                fontSize: "2.5rem",
                padding: ".25rem",
                color: "#FFF",
                backgroundColor: "#c15700",
                borderRadius: "50%",
              }}
            />
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ padding: ".25rem 0 0 .5rem", color: "#c15700", textWrap: "nowrap" }}>
              Record Intake
            </Typography>
          </Box>
          <Box width="40%">
            <Typography
              variant="h6"
              fontWeight="bold"
              color="#545454"
              textAlign="center"
            >
              {currentOrder.firstName} {currentOrder.lastName}
            </Typography>
          </Box>
          <Box
            sx={{
              marginRight: "1rem",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-end",
              width: "20%"
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              color="#c15700"
              width="100%"
              textAlign="end"
            >
              {currentOrder?.mealName}
            </Typography>
            <Typography
              variant="body2"
              color="#c15700"
              width="100%"
              textAlign="end"
            >
              {dayjs(currentOrder?.mealDate).format("dddd, MM/DD/YYYY")}
            </Typography>
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ paddingLeft: ".5rem", marginBottom: "1.5rem" }}
        >
          <Typography
            variant="body2"
            textAlign="center"
            width="25%"
          >
            <b>Average Intake:</b> {getAverageIntake()}
          </Typography>
          <Box
            sx={{
              marginRight: "1rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "1px solid gray",
              borderRadius: "var(--borderRadius)",
              padding: ".5rem .75rem"
            }}
          >
            {/* <Typography fontWeight="bold" variant="body2">
              QUICK ACTIONS
            </Typography> */}
            <Button
              variant="outlined"
              sx={{
                color: "#12850e",
                border: "1px solid #12850e",
                fontWeight: "bold",
              }}
              startIcon={<CheckCircleOutlined sx={{fontSize: "1.5rem !important"}}/>}
              onClick={() => handleQuickAction(100)}
            >
              ALL 100%
            </Button>
            <Button
              variant="outlined"
              sx={{
                color: "#dd9900",
                border: "1px solid #fbae13",
                fontWeight: "bold",
                marginLeft: ".5rem"
              }}
              startIcon={<ContrastOutlined sx={{fontSize: "1.5rem !important"}}/>}
              onClick={() => handleQuickAction(50)}
            >
              ALL 50%
            </Button>
            <Button
              variant="outlined"
              sx={{
                color: "#545454",
                border: "1px solid #545454",
                fontWeight: "bold",
                marginLeft: ".5rem"
              }}
              startIcon={<CancelOutlined sx={{fontSize: "1.5rem !important"}} />}
              onClick={() => handleQuickAction(0)}
            >
              ALL 0%
            </Button>
          </Box>
        </Box>

        <Box 
          className="customScrollbar"
          sx={{ 
            padding: "0 1.5rem", 
            marginBottom: "1.5rem", 
            overflowY: "auto",
            height: "275px"
          }}
        >
          {groupedOrderItems.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #e0e0e0",
                padding: ".5rem 0"
              }}
            >
              <Box>
                <Typography fontWeight="bold" variant="body2">
                  {item.recipeName}
                </Typography>
                {!!item?.subItems?.length && (
                  <Typography variant="caption">
                    {/* \u2022 is the bullet point unicode */}
                    {item.subItems.map((sub) => sub.recipeName).join(" \u2022 ")} 
                  </Typography>
                )}
              </Box>
              <Box display="flex">
                {PERCENT_OPTIONS.map(({ value, color }) => {
                  const isSelected = intakeMap[item.id] === value;
                  return (
                    <Button
                      key={value}
                      variant="outlined"
                      onClick={() => handleSelectPercent(item.id, value)}
                      sx={{
                        minWidth: "3.75rem",
                        marginLeft: ".5rem",
                        fontWeight: "bold",
                        color: isSelected ? "#FFF" : color,
                        backgroundColor: isSelected ? color : "transparent",
                        border: `1px solid ${color}`,
                        "&:hover": {
                          backgroundColor: isSelected ? color : "transparent",
                        }
                      }}
                    >
                      {`${value}%`}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ padding: "0 1.5rem", marginBottom: ".25rem" }}>
          <BaseInput
            control={control}
            errors={errors}
            id="intakeNote"
            name="intakeNote"
            label="Notes (optional)"
            placeholder="Add a note about this meal..."
            multiline
            variant="outlined"
            rows="2"
            sx={{ width: "60%" }}
            validationProps={{
              maxLength: MAX_LENGTH_VALIDATION("Note", 200)
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "0 1.5rem",
            marginBottom: "1rem"
          }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: "var(--yellowColor)",
              fontWeight: "bold",
              color: "#000"
            }}
            onClick={() => handleClose()}
          >
            Cancel
          </Button>
          <Button
            sx={{
              width: "fit-content",
              alignSelf: "flex-end",
              fontWeight: "bold",
              color: "#FFF",
            }}
            variant="contained"
            onClick={() => {
              handleSubmit(onSubmit)();
            }}
          >
            {currentOrder?.intakeAverage != null ? "Update Intake" : "Save Intake"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}